import React from 'react';
import { Link } from 'react-router-dom';
import {
  Cpu,
  Activity,
  LineChart,
  ShieldCheck,
  CheckCircle2,
  Database,
  Smartphone,
  FileText,
  Presentation,
  Bot,
  Mail,
  ArrowRight,
} from 'lucide-react';

export const WhatWeDo: React.FC = () => {
  const capabilities = [
    {
      num: '01',
      title: 'Autonomous Ingestion via WhatsApp, Voice & Email',
      icon: Bot,
      desc: 'Send voice notes over WhatsApp ("Paid ₹2,400 for packaging via UPI"), forward vendor PDFs to a dedicated inbox (invoices@company.forty2.ai), or map proprietary Excel sheets without code. Forty2 automatically parses line items and stages vouchers for approval.',
      pill: 'Zero Clerical Work',
    },
    {
      num: '02',
      title: 'Continuous Anomaly & Leakage Detection',
      icon: Activity,
      desc: 'The system continuously monitors your numbers. It detects fuel station dip variances, Swiggy/Zomato commission overcharges, and uncredited PineLabs card batches as they happen — so discrepancies are caught immediately, not after month-end.',
      pill: 'Real-Time Auditing',
    },
    {
      num: '03',
      title: 'Native TallyPrime & ERP Background Synchronization',
      icon: Database,
      desc: 'Integrates natively with TallyPrime, Zoho Books, and QuickBooks. Our ultra-lightweight sync connector runs silently in the background without slowing your accountant down or requiring system migration.',
      pill: 'Zero Migration',
    },
    {
      num: '04',
      title: '1-Click Board MIS & PowerPoint (.pptx) Decks',
      icon: Presentation,
      desc: 'Transform raw general ledgers into publication-grade PowerPoint presentations in one click. Complete with executive ratio matrices, EBITDA walks, cash runway burn analysis, and automated Statement of Accounts follow-up via WhatsApp.',
      pill: 'Executive Visibility',
    },
    {
      num: '05',
      title: 'Automated Form 3CD & MSME 43B(h) Statutory Sentinel',
      icon: ShieldCheck,
      desc: 'Active compliance rules safeguard against Section 40A(3) cash ceilings, monitor the 15/45-day MSME payment countdown to avoid corporate tax disallowance, and reconcile GSTR-2B against books to reclaim 100% of eligible Input Tax Credit.',
      pill: 'Statutory Protection',
    },
    {
      num: '06',
      title: 'Chartered Accountant Firm Accountability Guaranteed',
      icon: CheckCircle2,
      desc: 'Every client engagement is supported by qualified Chartered Accountants. You get cutting-edge autonomous intelligence coupled with rigorous statutory audit working papers and certified professional oversight.',
      pill: 'CA Rigor Guaranteed',
    },
  ];

  const pillars = [
    {
      title: 'AI-Powered Bookkeeping',
      desc: 'Automated capture, reconciliation, and clean structured books — always up to date.',
      icon: Smartphone,
    },
    {
      title: 'Intelligent Reporting',
      desc: 'Dashboards, budget vs actual, cash flow forecasts, and profitability breakdowns.',
      icon: FileText,
    },
    {
      title: 'CA-Backed Compliance',
      desc: 'Professional oversight ensuring accuracy, compliance, and peace of mind.',
      icon: ShieldCheck,
    },
    {
      title: 'Chartered Accountant Firm Backing',
      desc: 'Every engagement is supported by qualified CA professionals. You get the intelligence of AI with the rigour of professional accountancy — ensuring your books are always accurate and compliant.',
      icon: CheckCircle2,
    },
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero Header */}
      <section
        style={{
          paddingTop: '64px',
          paddingBottom: '80px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'radial-gradient(ellipse 60% 50% at 50% 0%, var(--brand-red-glow-subtle), transparent)',
        }}
      >
        <div className="container-max" style={{ textAlign: 'center', maxWidth: '800px' }}>
          <span className="badge-pill badge-red" style={{ marginBottom: '16px' }}>
            OUR SERVICES
          </span>
          <h1
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.8rem)',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              marginBottom: '20px',
            }}
          >
            What We Do
          </h1>
          <p
            style={{
              fontSize: '1.25rem',
              color: 'var(--text-primary)',
              lineHeight: '1.6',
              marginBottom: '14px',
              fontWeight: 500,
            }}
          >
            Forty2 manages your entire finance function — from accounting and bookkeeping to reporting and financial intelligence.
          </p>
          <p
            className="font-serif"
            style={{
              fontSize: '1.4rem',
              fontStyle: 'italic',
              color: 'var(--brand-red-light)',
            }}
          >
            We don't just record your numbers. We make them work for your business.
          </p>
        </div>
      </section>

      {/* 5 Core Capabilities Section */}
      <section style={{ padding: '96px 0' }}>
        <div className="container-max">
          <div style={{ maxWidth: '640px', marginBottom: '56px' }}>
            <span className="badge-pill badge-neutral font-mono" style={{ marginBottom: '12px' }}>
              CAPABILITIES
            </span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, color: 'var(--text-primary)' }}>
              Autonomous Ingestion, Continuous Tracking & CA Rigor
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div
                  key={cap.num}
                  className="glass-panel"
                  style={{
                    padding: '36px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '24px',
                    alignItems: 'center',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        backgroundColor: 'rgba(229, 57, 53, 0.12)',
                        border: '1px solid rgba(229, 57, 53, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={26} color="var(--brand-red)" />
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <span className="font-mono" style={{ fontSize: '0.85rem', color: 'var(--brand-red)', fontWeight: 700 }}>
                          {cap.num}
                        </span>
                        <span className="badge-pill badge-neutral" style={{ fontSize: '0.7rem' }}>
                          {cap.pill}
                        </span>
                      </div>
                      <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {cap.title}
                      </h3>
                    </div>
                  </div>

                  <div>
                    <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                      {cap.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4 Feature Pillars Grid */}
      <section
        style={{
          padding: '96px 0',
          backgroundColor: 'var(--bg-surface)',
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div className="container-max">
          <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 56px auto' }}>
            <span className="badge-pill badge-red" style={{ marginBottom: '14px' }}>
              DELIVERY MODEL
            </span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, color: 'var(--text-primary)' }}>
              Four Pillars of Forty2 Delivery
            </h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '24px',
            }}
          >
            {pillars.map((pillar, i) => {
              const Icon = pillar.icon;
              return (
                <div
                  key={i}
                  className="glass-card-interactive"
                  style={{
                    padding: '32px 26px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        background: 'var(--border-subtle)',
                        border: '1px solid var(--border-subtle)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px',
                      }}
                    >
                      <Icon size={22} color="var(--brand-red)" />
                    </div>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                      {pillar.title}
                    </h3>
                    <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                      {pillar.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Dedicated Industry Solutions Banner */}
      <section style={{ padding: '64px 0', borderBottom: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-deep)' }}>
        <div className="container-max">
          <div
            className="glass-panel-elevated"
            style={{
              padding: '40px 48px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '24px',
              borderRadius: '16px',
              border: '1px solid var(--border-medium)',
            }}
          >
            <div style={{ maxWidth: '640px' }}>
              <span className="badge-pill badge-red" style={{ marginBottom: '10px' }}>
                VERTICAL TAILORING
              </span>
              <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Looking for Fuel Station, F&B, or Real Estate Intelligence?
              </h3>
              <p style={{ fontSize: '0.98rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                Discover our specialized workflows for BPCL petrol pumps, Swiggy/Zomato reconciliations, and construction milestone billing.
              </p>
            </div>
            <Link to="/solutions" className="btn-primary" style={{ padding: '14px 28px', whiteSpace: 'nowrap' }}>
              <span>View Industry Solutions</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ padding: '96px 0', textAlign: 'center' }}>
        <div className="container-max" style={{ maxWidth: '720px' }}>
          <h2
            style={{
              fontSize: 'clamp(2rem, 3.8vw, 2.75rem)',
              fontWeight: 800,
              color: 'var(--text-primary)',
              marginBottom: '20px',
            }}
          >
            Ready to move from accounting to financial intelligence?
          </h2>
          <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '36px' }}>
            Book a consultation with our Chartered Accountant partners and discover how Forty2 brings structure and clarity to your finances.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" style={{ padding: '16px 36px', fontSize: '1.05rem' }}>
              <span>Talk to Us →</span>
            </Link>
            <Link to="/diagnostic" className="btn-secondary" style={{ padding: '16px 28px', fontSize: '1.05rem' }}>
              <span>Take Diagnostic First</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
